import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { ItemCreate, ItemDetail } from "@sable/api-contract";
import { CONDITION_GRADES, defaultWeightUnit, gramsFromParts, type ConditionGrade } from "@sable/domain";
import { PhotoSlots, type PendingPhoto } from "../components/PhotoSlots.tsx";
import { Field, MoneyInput, WeightFields, type WeightParts } from "../components/ui.tsx";
import { t, tk } from "../i18n/index.ts";
import { request } from "../lib/api.ts";
import { keys, uploadPhoto, requestEnrich } from "../lib/queries.ts";
import { useWs, wsPath } from "../lib/workspace.tsx";

interface Draft {
  title: string;
  brand: string;
  category: string;
  sizeLabel: string;
  conditionGrade: ConditionGrade | "";
  purchasePriceMinor: number | null;
  tagPriceMinor: number | null;
  sourceName: string;
  storageBin: string;
  itemWeight: WeightParts;
  isPersonal: boolean;
}

const EMPTY: Draft = {
  title: "", brand: "", category: "", sizeLabel: "", conditionGrade: "", purchasePriceMinor: null,
  tagPriceMinor: null, sourceName: "", storageBin: "", itemWeight: { major: "", minor: "" }, isPersonal: false,
};

export function NewItem() {
  const ws = useWs();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const busy = progress !== null;

  async function save(another: boolean) {
    setError(null);
    setProgress({ done: 0, total: photos.length });
    const trim = (s: string) => s.trim() || null;
    let itemGrams: number | null;
      try {
      itemGrams = gramsFromParts(draft.itemWeight.major, draft.itemWeight.minor, ws.units);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setProgress(null);
      return;
    }
    const body: ItemCreate = {
      status: "in_stock",
      title: trim(draft.title),
      brand: trim(draft.brand),
      category: trim(draft.category),
      sizeLabel: trim(draft.sizeLabel),
      conditionGrade: draft.conditionGrade || null,
      purchasePriceMinor: draft.purchasePriceMinor,
      tagPriceMinor: draft.tagPriceMinor,
      sourceName: trim(draft.sourceName),
      storageBin: trim(draft.storageBin),
      itemWeightGrams: itemGrams,
      itemWeightUnit: itemGrams === null ? null : defaultWeightUnit(ws.units),
      isPersonal: draft.isPersonal,
    };
    let item: ItemDetail;
    try {
      item = await request<ItemDetail>("/items", { method: "POST", workspace: ws.id, body });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setProgress(null);
      return;
    }
    // The item exists now; a failed photo is reported but doesn't lose the item.
    let failed: string | null = null;
    for (const [i, p] of photos.entries()) {
      try {
        await uploadPhoto(ws.id, item.id, p.slot, p.photo);
      } catch (err) {
        failed ??= err instanceof Error ? err.message : String(err);
      }
      setProgress({ done: i + 1, total: photos.length });
    }
    photos.forEach((p) => URL.revokeObjectURL(p.photo.previewUrl));
    // Photos in: the AI reads them and fills in what was left empty. The item page shows its progress.
    if (photos.length > 0 && (!failed || photos.length > 1)) await requestEnrich(ws.id, item.id).catch(() => undefined);
    void qc.invalidateQueries({ queryKey: keys.items(ws.id) });
    if (failed) {
      navigate(wsPath(ws, `/items/${item.id}`), { state: { notice: t("intake.photoFailed", { message: failed }) } });
      return;
    }
    if (another) {
      setDraft({ ...EMPTY, sourceName: draft.sourceName, storageBin: draft.storageBin });
      setPhotos([]);
      setProgress(null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(wsPath(ws, `/items/${item.id}`));
    }
  }

  return (
    <form className="stack-lg" onSubmit={(e) => { e.preventDefault(); void save(false); }}>
      <div className="page-head"><h1>{t("intake.title")}</h1></div>

      <section className="card">
        <div className="section-head">
          <h2>{t("intake.photos")}</h2>
        </div>
        <p className="muted small">{t("intake.photosHint")}</p>
        <div className="divider" />
        <PhotoSlots photos={photos} onChange={setPhotos} />
      </section>

      <section className="card stack">
        <h2>{t("intake.details")}</h2>
        <Field label={t("intake.titleField")} optional>
          <input className="input" value={draft.title} maxLength={280} placeholder={t("intake.titlePlaceholder")} onChange={(e) => set("title", e.target.value)} />
        </Field>
        <div className="grid-2">
          <Field label={t("intake.brand")} optional>
            <input className="input" value={draft.brand} maxLength={120} onChange={(e) => set("brand", e.target.value)} />
          </Field>
          <Field label={t("intake.category")} optional>
            <input className="input" value={draft.category} maxLength={80} onChange={(e) => set("category", e.target.value)} />
          </Field>
          <Field label={t("intake.size")} optional>
            <input className="input" value={draft.sizeLabel} maxLength={40} onChange={(e) => set("sizeLabel", e.target.value)} />
          </Field>
          <Field label={t("intake.condition")} optional>
            <select className="select" value={draft.conditionGrade} onChange={(e) => set("conditionGrade", e.target.value as ConditionGrade | "")}>
              <option value="">—</option>
              {CONDITION_GRADES.map((g) => <option key={g} value={g}>{tk("condition", g)}</option>)}
            </select>
          </Field>
          <Field label={t("intake.purchasePrice")} optional>
            <MoneyInput currency={ws.homeCurrency} locale={ws.locale} value={draft.purchasePriceMinor} onChange={(v) => set("purchasePriceMinor", v)} />
          </Field>
          <Field label={t("intake.tagPrice")} optional>
            <MoneyInput currency={ws.homeCurrency} locale={ws.locale} value={draft.tagPriceMinor} onChange={(v) => set("tagPriceMinor", v)} />
          </Field>
          <Field label={t("intake.source")} optional>
            <input className="input" value={draft.sourceName} maxLength={120} placeholder={t("intake.sourcePlaceholder")} onChange={(e) => set("sourceName", e.target.value)} />
          </Field>
          <Field label={t("intake.bin")} optional>
            <input className="input" value={draft.storageBin} maxLength={40} onChange={(e) => set("storageBin", e.target.value)} />
          </Field>
          <Field label={t("intake.itemWeight")} hint={t("intake.weightHint")} optional>
            <WeightFields label={t("intake.itemWeight")} value={draft.itemWeight} units={ws.units} onChange={(v) => set("itemWeight", v)} />
          </Field>
        </div>
        {ws.personalItems && (
          <label className="check">
            <input type="checkbox" checked={draft.isPersonal} onChange={(e) => set("isPersonal", e.target.checked)} />
            {t("intake.personal")}
          </label>
        )}
      </section>

      {error && <p className="form-error" role="alert">{error}</p>}
      {progress && progress.total > 0 && <p className="muted" role="status">{t("intake.uploading", progress)}</p>}
      <div className="row-wrap">
        <button className="btn btn-primary" disabled={busy}>{busy ? t("app.saving") : t("intake.save")}</button>
        <button type="button" className="btn" disabled={busy} onClick={() => void save(true)}>{t("intake.saveAnother")}</button>
      </div>
    </form>
  );
}
